/** @jsx jsx */
import { jsx } from 'theme-ui';
import { BuilderComponent, BuilderContent, Builder } from '@builder.io/react';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import * as queryString from 'query-string';

import '../builder-settings';

import Layout from '../components/layout';
import { setupServersideConfig } from '../utilities/customSetupServerside.js';
import { setupContentLoadedDataLayer, setupCookiesClientside } from '../utilities/customSetupClientside.js';
import Header from '../components/header/header';
import WelcomeBanner from '../components/header/WelcomeBanner';

/*
~ Important Gotchas ~
* This file is executed *both* in the Serverside Rendering (SSR) and Clientside on page load.
* If there is an error in the logic in this file while a user tries to access a marketingPage, the NotFoundPage and status_code=404 may be served instead, despite the correct status_code being 500
* The only reliable way for clientside logic to access an A/B-test-page's variant data is via the clientside contentLoaded hook.
** The pageData from graphql has data on variants, but it can't be used clientside for (e.g.) "Variant B was served to the user; push X into the dataLayer"
** Therefore, you should not implement variant logic here. Either do it in the Builder GUI, or have logic in GTM that reads and acts on BLDR_VARIANT_NAME
* DataLayer variables set: ["BLDR_PAGE_NAME", "BLDR_PAGE_ID", "BLDR_VARIANT_NAME", "BLDR_VARIANT_ID", "BLDR_VARIANT_TEST_RATIO", "country", "LS_VARIANT", "omCampaignId"] plus the k:v {"event": "enableOM"}
*/

const ContentPage = ({ data, location }) => {
  const [loaded, setLoaded] = useState(false);                        // A React Hook that keeps track if the contentLoaded hook has been run, preventing a bug specific to our setup where the contentLoaded Hook can/will run twice
  var { pageData, regionConfig } = setupServersideConfig( data, location );
  const urlParams = location ? queryString.parse(location.search) : {}; 

  // Adding top margin to the main content to account for the floating nav
  var navMargin = '70px';

  // Only show welcome banner if brand is Harry's AND `name` query parameter is present
  var showWelcomeBanner = urlParams.name || (urlParams.offer && urlParams.code) ? true: false;
  const gtmUrl = `https://www.googletagmanager.com/ns.html?id=${regionConfig.tagContainerId}`;

  return (
    <Layout                                                            // Dynamically generate Header, Footer, and GTM Noscript IFrame based on country and language of page
      backgroundColor="white"
      regionConfig={regionConfig}
    >
      <Helmet>
        <title>{regionConfig.defaultTitle}</title>
        {(pageData && pageData?.data?.hideFromSearchEngines) &&
          <meta name="robots" content="noindex" />
        }
        <link rel="shortcut icon" type="image/x-icon" href="https://cdn.shopify.com/oxygen-v2/38328/30647/64037/1492918/assets/favicon-BWMJS88x.ico" />;
        <meta
          name="description"
          content={(pageData && pageData?.data?.description) || regionConfig.defaultDescription}
        />
        <script type="text/javascript" innerHTML={regionConfig.headGTMScript} />
        {Builder.isEditing && 
          <script src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" id="trustpilot-script"></script>
        }
      </Helmet>

      <BuilderContent content={pageData} model="page">
        { (variation, loading, content ) => {
          var variantData,
              pageProperties = variation,
              hideNav = variation?.hideEntireNav,
              welcomeBannerUrl = content?.data?.welcomeBannerUrl ? content.data.welcomeBannerUrl : regionConfig.welcomeBannerUrl;

          // If target page custom field is set - replace page content with target page's content.
          if(variation && variation.targetPage && variation.targetPage.value) {
            var variationName = variation.targetPage.value.name;
            
            pageData = variation.targetPage.value;

            variantData = {
              pageName: content.name,
              name: variationName,
              path: variation.targetPage.value.data.url,
              id: content.testVariationId,
              testRatio: content.testRatio
            };   

            // Use the page properties of the variation's target page
            pageProperties = pageData.data;
            hideNav = pageProperties?.hideEntireNav;

            welcomeBannerUrl = variation.targetPage.value.data.welcomeBannerUrl ? variation.targetPage.value.data.welcomeBannerUrl : welcomeBannerUrl;
          }

          return (
            <div>
              {!hideNav && regionConfig?.region && <Header regionConfig={regionConfig} pageData={pageProperties} />}
              {showWelcomeBanner && 
              <WelcomeBanner
                brand={regionConfig.brand}
                urlParams={urlParams}
                url={welcomeBannerUrl}
              />}
              {regionConfig?.bodyGTMIframe &&
                <noscript>
                  <iframe title="gtm-noscript" src={gtmUrl} height="0" width="0" style={{display:'none',visibility:'hidden'}}></iframe>
                </noscript>
              }
              {showWelcomeBanner &&
                <div style={{height:'48px'}}></div>
              }
              <div style={{height:navMargin}}></div>
            <BuilderComponent
              model={regionConfig.modelName}
              content={pageData}                                              // this line ensures all non-Header/Footer content is ServerSide-Rendered
              contentLoaded={(_, content) => {                                // this React Hook executes clientside only. It runs twice, undesirably
    
                if (!Builder.isEditing && !loaded) {                         // This prevents GTM from loading and populating e.g. OptinMonster popups for the builder.io/content UI. Also ensure it only runs once.
                  setLoaded(true);
                  setupContentLoadedDataLayer( pageData, regionConfig, content, variantData );
                  setupCookiesClientside(regionConfig, location, pageData);
                }
              }}
            />
            </div>
          )}
       }     
      </BuilderContent>
    </Layout>
  );
};

export default ContentPage;
